import React, {
  ReactElement, useCallback, useEffect, useMemo, useRef, useState,
} from "react"
import { offset } from "@floating-ui/react"
import {
  Box, CloseButton,
  FormControl,
  FormHelperText,
  FormLabel,
  Flex,
  Input,
  InputGroup, InputRightElement,
  Portal,
  Text,
} from "@chakra-ui/react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "components/inputs/DatePickerField.css"
import "components/inputs/DatePickerField.modern.css"
import { toZonedTime, fromZonedTime } from "date-fns-tz"
import { useCurrentEvent } from "queries/events"
import {
  Control, ControllerRenderProps, FieldValues, useController,
} from "react-hook-form"
import {
  endOfDay, isSameDay, isValid, startOfDay,
} from "date-fns"
import { DateFormat } from "utilities/constants"

type Props<T extends FieldValues> = {
  name: string;
  control: Control<T, object>;
  label?: string;
  placeholder?: string;
  vertical?: boolean;
  utc?: boolean;
  showTimeSelect?: boolean;
  variant?: string;
  disabled?: boolean;
  minDate?: Date;
  helperText?: string;
  showDeleteButton?: boolean
  inline?: boolean
  open?: boolean
  shouldCloseOnSelect?: boolean
  customInput?: ReactElement
  calendarOffset?: number
  renderInPortal?: boolean
  onCalendarClose?: () => void
  intervalStartDate?: string
  intervalEndDate?: string
  selectsStart?: boolean
  selectsEnd?: boolean
  error?: boolean
  errorText?: string
  onValueChange?: (date: Date | null) => void
}

const AgendaDateTimeField = <T extends FieldValues, >({
  name,
  control,
  label,
  placeholder,
  vertical,
  utc,
  showTimeSelect,
  variant = "flushed",
  disabled,
  minDate,
  helperText,
  showDeleteButton = false,
  inline = false,
  open,
  shouldCloseOnSelect,
  customInput,
  calendarOffset = 0,
  renderInPortal,
  onCalendarClose,
  intervalStartDate,
  intervalEndDate,
  selectsStart,
  selectsEnd,
  error,
  errorText,
  onValueChange,
}: Props<T>) => {
  const {
    data: {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  } = useCurrentEvent()
  const { field } = useController<any>({ name, control })

  const utcToZonedUtility = (date: Date | undefined) => (date ? toZonedTime(new Date(date), utc ? "UTC" : timezone) : undefined)
  const intervalStartDateInDateFormat = intervalStartDate ? new Date(intervalStartDate) : undefined
  const intervalEndDateInDateFormat = intervalEndDate ? new Date(intervalEndDate) : undefined

  let selected: Date | null = null
  if (field.value && isValid(new Date(field.value))) {
    selected = utcToZonedUtility(field.value) as Date
  }

  const timeZone = utc ? "UTC" : timezone

  const calculateMinMaxTime = () => {
    let minTime

    if (!intervalStartDateInDateFormat || !intervalEndDateInDateFormat) {
      return { minTime: undefined, maxTime: undefined }
    }

    if (isSameDay(utcToZonedUtility(intervalStartDateInDateFormat)!,
      utcToZonedUtility(intervalEndDateInDateFormat)!) && minDate) {
      minTime = utcToZonedUtility(intervalStartDateInDateFormat)
    } else {
      minTime = startOfDay(new Date())
    }

    return { minTime, maxTime: endOfDay(new Date()) }
  }

  const { minTime, maxTime } = calculateMinMaxTime()

  const handleChangeZonedDate = useCallback((date: Date | null) => {
    const stored = date ? fromZonedTime(date, timeZone) : null
    field.onChange(stored)
    onValueChange?.(stored)
  }, [field, onValueChange, timeZone])

  const handleChangeDateOnly = useCallback((dateOnly: Date | null) => {
    if (!dateOnly) {
      handleChangeZonedDate(null)
      return
    }

    const base = selected ?? utcToZonedUtility(new Date()) ?? new Date()
    const next = new Date(dateOnly)
    next.setHours(base.getHours(), base.getMinutes(), 0, 0)
    handleChangeZonedDate(next)
  }, [handleChangeZonedDate, selected, utcToZonedUtility])

  const splitDateAndTime = !!showTimeSelect && !customInput && !inline

  return (
    <FormControl
      mb={4}
      display="flex"
      alignItems="flex-start"
      flexDirection={vertical ? "column" : "row"}
      isDisabled={disabled}
      isInvalid={error}
    >
      {label && (
        <FormLabel
          w={vertical ? "full" : "25%"}
          fontSize="md"
          mt={2}
        >
          {label}
        </FormLabel>
      )}
      <Box w="100%">
        {splitDateAndTime ? (
          <Flex gap={2} align="flex-start">
            <Box flex="0 0 220px">
              <DatePicker
                selectsStart={selectsStart}
                selectsEnd={selectsEnd}
                startDate={intervalStartDateInDateFormat}
                endDate={intervalEndDateInDateFormat}
                open={open}
                shouldCloseOnSelect={shouldCloseOnSelect}
                selected={selected}
                onChange={handleChangeDateOnly}
                onCalendarClose={() => {
                  field.onBlur()
                  onCalendarClose?.()
                }}
                dateFormat={DateFormat.MONTH_DAY_YEAR}
                placeholderText={placeholder ?? "Date"}
                calendarClassName="datepicker-theme-modern"
                showPopperArrow
                customInput={(
                  <CustomInput
                    variant={variant}
                    field={field}
                    disabled={disabled}
                    showDeleteButton={showDeleteButton}
                    placeholder="Date"
                  />
                )}
                disabled={disabled}
                minDate={utcToZonedUtility(minDate)}
                popperModifiers={[
                  offset(calendarOffset),
                ]}
                popperContainer={
                  renderInPortal
                    ? ({ children }) => <Portal>{children}</Portal>
                    : undefined
                }
              />
            </Box>
            <Box w="110px">
              <TimeInputWithDropdown
                variant={variant}
                value={selected}
                onChange={handleChangeZonedDate}
                onBlur={field.onBlur}
                disabled={disabled}
                placeholder="Time"
                minTime={minTime}
                maxTime={maxTime}
                error={error}
              />
            </Box>
          </Flex>
        ) : (
          <DatePicker
            timeIntervals={15}
            selectsStart={selectsStart}
            selectsEnd={selectsEnd}
            startDate={intervalStartDateInDateFormat}
            endDate={intervalEndDateInDateFormat}
            inline={inline}
            open={open}
            shouldCloseOnSelect={shouldCloseOnSelect}
            showTimeSelect={showTimeSelect}
            showPopperArrow
            selected={selected}
            onChange={handleChangeZonedDate}
            onCalendarClose={() => {
              field.onBlur()
              onCalendarClose?.()
            }}
            dateFormat={showTimeSelect ? "MMMM d, yyyy h:mm aa" : DateFormat.MONTH_DAY_YEAR}
            placeholderText={placeholder}
            calendarClassName="datepicker-theme-modern"
            customInput={customInput || (
              <CustomInput
                variant={variant}
                field={field}
                disabled={disabled}
                showDeleteButton={showDeleteButton}
                placeholder={placeholder}
              />
            )}
            disabled={disabled}
            minDate={utcToZonedUtility(minDate)}
            minTime={minTime}
            maxTime={maxTime}
            popperModifiers={[
              offset(calendarOffset),
            ]}
            popperContainer={
              renderInPortal
                ? ({ children }) => <Portal>{children}</Portal>
                : undefined
            }
          />
        )}
        {error && errorText && (
          <FormHelperText as="div" mt={2}>
            <Text fontSize="md" fontWeight="semibold" color="red.500">{errorText}</Text>
          </FormHelperText>
        )}
        {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Box>
    </FormControl>
  )
}

type CustomInputProps = {
  variant: string;
  disabled?: boolean;
  showDeleteButton?: boolean;
  field: ControllerRenderProps<any, string>;
  onChange?: () => void;
  onBlur?: (e: React.FocusEvent) => void;
  placeholder?: string;
  value?: string;
  id?: string;
  onClick?: () => void;
}

const CustomInput = ({
  variant, disabled, showDeleteButton, field, onChange, onBlur, placeholder, value, id, onClick,
}: CustomInputProps) => (
  <InputGroup>
    <Input
      variant={variant}
      bg="white"
      color={disabled ? "gray.400" : undefined}
      onChange={onChange}
      onBlur={(e) => {
        field.onBlur()
        onBlur?.(e)
      }}
      placeholder={placeholder}
      value={value}
      id={id}
      onClick={onClick}
    />{(showDeleteButton && value) && (
    <InputRightElement w="auto"><CloseButton onClick={() => { field.onChange(null) }} />
    </InputRightElement>
    )}
  </InputGroup>
)

type TimeSegment = "hours" | "minutes" | "ampm"

const pad2 = (n: number) => String(n).padStart(2, "0")

const getTimeParts = (d: Date) => {
  const hours24 = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours24 >= 12 ? "PM" : "AM"
  const hours12 = ((hours24 + 11) % 12) + 1
  return {
    hours24,
    hours12,
    minutes,
    ampm: ampm as "AM" | "PM",
  }
}

const setTimeParts = (
  base: Date,
  hours12: number,
  minutes: number,
  ampm: "AM" | "PM",
) => {
  const clampedHours12 = Math.min(12, Math.max(1, hours12))
  const clampedMinutes = Math.min(59, Math.max(0, minutes))
  const hours24 = (clampedHours12 % 12) + (ampm === "PM" ? 12 : 0)
  const next = new Date(base)
  next.setHours(hours24, clampedMinutes, 0, 0)
  return next
}

type TimeInputWithDropdownProps = {
  variant: string
  value: Date | null
  onChange: (date: Date | null) => void
  onBlur: () => void
  disabled?: boolean
  placeholder?: string
  minTime?: Date
  maxTime?: Date
  error?: boolean
}

const TimeInputWithDropdown = ({
  variant,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder = "Time",
  minTime,
  maxTime,
  error,
}: TimeInputWithDropdownProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [segment, setSegment] = useState<TimeSegment>("hours")
  const [lastDigitAt, setLastDigitAt] = useState<number>(0)
  const [carryDigit, setCarryDigit] = useState<string | null>(null)

  const displayValue = useMemo(() => {
    if (!value) return ""
    const parts = getTimeParts(value)
    return `${pad2(parts.hours12)}:${pad2(parts.minutes)} ${parts.ampm}`
  }, [value])

  const ensureBase = useCallback(() => {
    if (value) return value
    return new Date()
  }, [value])

  const selectSegment = useCallback((nextSegment: TimeSegment) => {
    setSegment(nextSegment)
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) return
      if (nextSegment === "hours") el.setSelectionRange(0, 2)
      if (nextSegment === "minutes") el.setSelectionRange(3, 5)
      if (nextSegment === "ampm") el.setSelectionRange(6, 8)
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    selectSegment(segment)
  }, [isOpen, segment, selectSegment])

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
  }, [])

  const scheduleClose = useCallback(() => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false)
    }, 200)
  }, [clearCloseTimeout])

  const handleFocus = () => {
    clearCloseTimeout()
    setIsOpen(true)
    selectSegment("hours")
  }

  const handleBlur = () => {
    onBlur()
    scheduleClose()
  }

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const el = e.currentTarget
    const pos = el.selectionStart ?? 0
    if (pos <= 2) selectSegment("hours")
    else if (pos <= 5) selectSegment("minutes")
    else selectSegment("ampm")
  }

  const bump = useCallback((delta: number) => {
    const base = ensureBase()
    const parts = getTimeParts(base)
    if (segment === "hours") {
      const next = new Date(base)
      next.setHours(parts.hours24 + delta, parts.minutes, 0, 0)
      onChange(next)
      return
    }
    if (segment === "minutes") {
      const next = new Date(base)
      next.setHours(parts.hours24, parts.minutes + delta, 0, 0)
      onChange(next)
      return
    }
    // am/pm
    const next = new Date(base)
    next.setHours(parts.hours24 + (delta > 0 ? 12 : -12), parts.minutes, 0, 0)
    onChange(next)
  }, [ensureBase, onChange, segment])

  const setFromDigits = useCallback((digit: string) => {
    const now = Date.now()
    const base = ensureBase()
    const parts = getTimeParts(base)

    const withinCarryWindow = carryDigit && (now - lastDigitAt) < 1000
    const nextRaw = withinCarryWindow ? `${carryDigit}${digit}` : digit

    if (segment === "hours") {
      const asNum = Number(nextRaw)
      const nextHours = asNum === 0 ? 12 : asNum
      const next = setTimeParts(base, nextHours, parts.minutes, parts.ampm)
      onChange(next)
    } else if (segment === "minutes") {
      const asNum = Number(nextRaw)
      const nextMinutes = Math.min(59, asNum)
      const next = setTimeParts(base, parts.hours12, nextMinutes, parts.ampm)
      onChange(next)
    }

    if (withinCarryWindow) {
      setCarryDigit(null)
    } else {
      setCarryDigit(digit)
    }
    setLastDigitAt(now)
  }, [carryDigit, ensureBase, lastDigitAt, onChange, segment])

  const toggleAmPm = useCallback(() => {
    const base = ensureBase()
    const parts = getTimeParts(base)
    const nextAmPm = parts.ampm === "AM" ? "PM" : "AM"
    onChange(setTimeParts(base, parts.hours12, parts.minutes, nextAmPm))
  }, [ensureBase, onChange])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (e.key === "ArrowLeft") {
      e.preventDefault()
      selectSegment(segment === "ampm" ? "minutes" : "hours")
      if (segment === "minutes") selectSegment("hours")
      if (segment === "ampm") selectSegment("minutes")
      return
    }
    if (e.key === "ArrowRight") {
      e.preventDefault()
      if (segment === "hours") selectSegment("minutes")
      else if (segment === "minutes") selectSegment("ampm")
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      bump(1)
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      bump(-1)
      return
    }
    if (e.key.toLowerCase() === "a" || e.key.toLowerCase() === "p") {
      e.preventDefault()
      toggleAmPm()
      return
    }
    if (/^\d$/.test(e.key) && (segment === "hours" || segment === "minutes")) {
      e.preventDefault()
      setFromDigits(e.key)
    }
  }

  const handleTimePickerChange = (d: Date | null) => {
    if (!d) return
    onChange(d)
  }

  return (
    <Box position="relative" onMouseDown={clearCloseTimeout}>
      <InputGroup>
        <Input
          ref={inputRef}
          variant={variant}
          bg="white"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          borderColor={error ? "red.500" : undefined}
          borderWidth={error ? "2px" : undefined}
          boxShadow={error ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined}
          _hover={error ? { borderColor: "red.500" } : undefined}
        />
      </InputGroup>
      {isOpen && (
        <Box position="absolute" top="100%" left={0} mt={2} zIndex={20}>
          <Box position="relative">
            <Box
              position="absolute"
              top="-6px"
              left="50%"
              w="10px"
              h="10px"
              bg="white"
              borderLeft="1px solid"
              borderTop="1px solid"
              borderColor="gray.200"
              transform="translateX(-50%) rotate(45deg)"
            />
            <DatePicker
              inline
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              selected={value ?? ensureBase()}
              onChange={handleTimePickerChange}
              calendarClassName="datepicker-theme-modern"
              minTime={minTime}
              maxTime={maxTime}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AgendaDateTimeField
